import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from './dialog.service';
import { ToastService } from './toast.service';
import { DAY_MS, clearSnooze, isSnoozed, snooze } from './consent-snooze';

export interface GeoLocation {
  latitude: number;
  longitude: number;
  /** Radius of uncertainty in metres */
  accuracy: number;
}

export type LocationPermission = PermissionState | 'unsupported';

const SNOOZE_KEY = 'location_prompt_snoozed_until';
const DECLINE_SNOOZE_MS = 30 * DAY_MS;
const DISMISS_SNOOZE_MS = DAY_MS;

/** Reuse a recent fix instead of hitting the GPS/network on every call. */
const CACHE_TTL_MS = 5 * 60 * 1000;

const POSITION_OPTIONS: PositionOptions = {
  // City/street-level is enough; high accuracy is slow and drains battery.
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: CACHE_TTL_MS,
};

/**
 * Location consent ("soft ask") and access.
 *
 * Same pattern as PushConsentService: explain why with our confirmation
 * dialog first and only trigger the browser prompt when the user opts in,
 * because a "Block" on the native prompt is permanent.
 *
 *  - `getLocationIfPermitted()` never prompts — use it when attaching
 *    location to API calls. Returns null unless the user already allowed it.
 *  - `requestWithConsent()` shows the soft prompt (respecting snooze) and,
 *    on opt-in, the native prompt.
 *
 * Location is always optional: callers must work when it is null.
 */
@Injectable({ providedIn: 'root' })
export class LocationConsentService {
  private dialogService = inject(DialogService);
  private matDialog = inject(MatDialog);
  private toast = inject(ToastService);

  private cached: { value: GeoLocation; at: number } | null = null;
  private isPrompting = false;

  /** Geolocation only works in a secure context (HTTPS or localhost). */
  isSupported(): boolean {
    return 'geolocation' in navigator && window.isSecureContext;
  }

  /** Current permission without prompting. */
  async permissionState(): Promise<LocationPermission> {
    if (!this.isSupported()) return 'unsupported';
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state;
    } catch {
      // Older Safari has no Permissions API for geolocation — treat as unknown.
      return 'prompt';
    }
  }

  /** Location if the user has already granted it; never shows a prompt. */
  async getLocationIfPermitted(): Promise<GeoLocation | null> {
    if ((await this.permissionState()) !== 'granted') return null;
    return this.readPosition();
  }

  /**
   * Soft prompt → native prompt. Resolves with the location, or null if the
   * user declined, the prompt is snoozed, or the position couldn't be read.
   */
  async requestWithConsent(): Promise<GeoLocation | null> {
    const state = await this.permissionState();
    if (state === 'granted') return this.readPosition();
    if (state !== 'prompt' || isSnoozed(SNOOZE_KEY) || this.isPrompting) return null;

    // Don't stack on top of a dialog the user is working in.
    if (this.matDialog.openDialogs.length) return null;

    this.isPrompting = true;
    return new Promise<GeoLocation | null>((resolve) => {
      this.dialogService
        .confirmDialogOnClick({
          // TODO: confirm the stated purpose matches the privacy policy
          title: 'Share your location?',
          message:
            'Your approximate location is saved with this device to help keep your account secure. It is only read when you sign in or register for notifications. You can change this anytime from your browser settings.',
          saveText: 'Allow',
          cancelText: 'Not now',
          type: 'confirm',
          icon: 'location_on',
        })
        .subscribe((result) => {
          this.isPrompting = false;

          if (result?.confirm) {
            // Still inside the click handler — readPosition() calls
            // getCurrentPosition synchronously, so the browser treats the
            // prompt as user-initiated. Keep it the first thing here.
            this.readPosition(true).then(resolve);
          } else {
            snooze(SNOOZE_KEY, result?.discard ? DECLINE_SNOOZE_MS : DISMISS_SNOOZE_MS);
            resolve(null);
          }
        });
    });
  }

  /**
   * @param fromConsentPrompt report the outcome to the user (only right after
   * they chose "Allow", so silent background reads never show toasts).
   */
  private readPosition(fromConsentPrompt = false): Promise<GeoLocation | null> {
    if (this.cached && Date.now() - this.cached.at < CACHE_TTL_MS) {
      return Promise.resolve(this.cached.value);
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const value: GeoLocation = {
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy,
          };
          this.cached = { value, at: Date.now() };
          if (fromConsentPrompt) clearSnooze(SNOOZE_KEY);
          resolve(value);
        },
        (error) => {
          if (fromConsentPrompt) this.onPromptError(error);
          resolve(null);
        },
        POSITION_OPTIONS
      );
    });
  }

  private onPromptError(error: GeolocationPositionError): void {
    if (error.code === error.PERMISSION_DENIED) {
      // Denied in the native prompt (or dismissed — Chrome reports both the same way)
      snooze(SNOOZE_KEY, DISMISS_SNOOZE_MS);
      this.toast.info(
        'To share it later, allow location for this site in your browser settings.',
        'Location not shared',
        { duration: 8000 }
      );
    }
    // POSITION_UNAVAILABLE / TIMEOUT: permission may be granted; we'll read it next time.
  }
}
